import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';

interface SplitPaneProps {
  leftPane: React.ReactNode;
  rightPane: React.ReactNode;
  initialSplitPosition?: number;
  minSplitPosition?: number;
  maxSplitPosition?: number;
  isHorizontal?: boolean;
  hideResizer?: boolean;
  /** When set, split % is restored on mount and saved after each drag. */
  persistKey?: string;
}

const Container = styled('div')({
  display: 'flex',
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
});

const Pane = styled('div')({
  overflow: 'auto',
  position: 'relative',
  height: '100%',
  flexShrink: 0,
});

const Resizer = styled('div')<{ isHorizontal?: boolean }>(({ isHorizontal }) => ({
  width: isHorizontal ? '100%' : '4px',
  height: isHorizontal ? '4px' : '100%',
  backgroundColor: '#9e9e9e',
  cursor: isHorizontal ? 'row-resize' : 'col-resize',
  position: 'relative',
  userSelect: 'none',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: '#757575',
  },
}));

function readPersistedSplit(
  key: string | undefined,
  fallback: number,
  min: number,
  max: number,
): number {
  if (!key) {
    return fallback;
  }
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return fallback;
    }
    return Math.min(Math.max(value, min), max);
  } catch {
    return fallback;
  }
}

const SplitPane: React.FC<SplitPaneProps> = ({
  leftPane,
  rightPane,
  initialSplitPosition = 50,
  minSplitPosition = 20,
  maxSplitPosition = 80,
  isHorizontal = false,
  hideResizer = false,
  persistKey,
}) => {
  const [splitPosition, setSplitPosition] = useState(() =>
    readPersistedSplit(persistKey, initialSplitPosition, minSplitPosition, maxSplitPosition),
  );
  const splitPositionRef = useRef(splitPosition);
  splitPositionRef.current = splitPosition;
  const resizerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startPositionRef = useRef(0);
  const startSplitPositionRef = useRef(0);

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current || !resizerRef.current) return;

    const container = resizerRef.current.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const delta = isHorizontal
      ? e.clientY - startPositionRef.current
      : e.clientX - startPositionRef.current;
    
    const containerSize = isHorizontal ? containerRect.height : containerRect.width;
    const deltaPercent = (delta / containerSize) * 100;
    const newPosition = startSplitPositionRef.current + deltaPercent;
    
    // Limit the split position between min and max values
    const limitedPosition = Math.min(Math.max(newPosition, minSplitPosition), maxSplitPosition);
    setSplitPosition(limitedPosition);
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
    if (persistKey) {
      try {
        localStorage.setItem(persistKey, String(splitPositionRef.current));
      } catch {
        // ignore quota / private mode
      }
    }
  };

  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    startPositionRef.current = isHorizontal ? e.clientY : e.clientX;
    startSplitPositionRef.current = splitPosition;
    
    document.body.style.cursor = isHorizontal ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
    
    // Add event listeners immediately
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, []);

  return (
    <Container sx={{ flexDirection: isHorizontal ? 'column' : 'row' }}>
      <Pane sx={{ 
        width: isHorizontal ? '100%' : `${splitPosition}%`,
        height: isHorizontal ? `${splitPosition}%` : '100%',
      }}>
        {leftPane}
      </Pane>
      {!hideResizer && (
        <Resizer
          ref={resizerRef}
          onMouseDown={handleResizerMouseDown}
          isHorizontal={isHorizontal}
        />
      )}
      <Pane sx={{ 
        width: isHorizontal ? '100%' : `${100 - splitPosition}%`,
        height: isHorizontal ? `${100 - splitPosition}%` : '100%',
      }}>
        {rightPane}
      </Pane>
    </Container>
  );
};

export default SplitPane; 